import { useEffect, useMemo, useState } from "react";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import type {
  ClusterArticle,
  ClusterDetailResponse,
  ClusterSettingsPayload,
  ClusterSettingsResponse,
  ClusterSummaryEntry,
  CryptoBubble,
  InstrumentSnapshot,
  LiquidityFlowPoint,
  LiquidityLevel,
  NewsFeedItem,
  SentimentIndicator,
  WatchlistEntry,
  CollapseMonitorResponse,
  CollapseSignal,
  CollapseTimelineEvent,
  CollapseAction,
} from "@app/services/marketDataApi";
import {
  useClusterDetail,
  useClusterNotification,
  useClusterSettings,
  useClusterSummary,
  useCryptoBubbles,
  useDiscardCluster,
  useInstrumentSnapshot,
  useNewsFeed,
  useCollapseMonitor,
  useExecuteCollapseAction,
  useSentimentData,
  useTriggerClusterAnalysis,
  useUpdateClusterSettings,
  useWatchlistData,
} from "./hooks/useMarketDataQueries";
import { useUiStore } from "@app/state/uiStore";
import "./marketData.css";

type NoticeState = { type: "success" | "error"; message: string } | null;

type ClusterStatusLabel = {
  label: string;
  tone: "neutral" | "positive" | "warning" | "danger";
};

export function formatChange(value: number | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "0.00%";
  }
  const formatted = `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
  return formatted;
}

export function classForChange(value: number | undefined): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "";
  }
  if (value > 0) {
    return "change-positive";
  }
  if (value < 0) {
    return "change-negative";
  }
  return "";
}

export function liquidityClass(level: LiquidityLevel): string {
  switch (level) {
    case "medium":
      return "liquidity-pill liquidity-pill--medium";
    case "low":
      return "liquidity-pill liquidity-pill--low";
    default:
      return "liquidity-pill";
  }
}

export function bubbleStyle(point: LiquidityFlowPoint): { width: string } {
  const base = 120;
  const scaled = Math.min(Math.max(Math.abs(point.netFlowUsd) / 1_000_000, 0.35), 2);
  const size = base * scaled;
  return { width: `${size}px` };
}

export function bubbleClass(point: LiquidityFlowPoint): string {
  if (point.direction === "inflow") {
    return "liquidity-bubble liquidity-bubble--inflow";
  }
  if (point.direction === "outflow") {
    return "liquidity-bubble liquidity-bubble--outflow";
  }
  return "liquidity-bubble liquidity-bubble--neutral";
}

export function formatFlow(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000_000) {
    return `${sign}$${(absValue / 1_000_000_000).toFixed(1)}B`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}$${(absValue / 1_000).toFixed(1)}K`;
  }
  return `${sign}$${absValue.toFixed(0)}`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    // Use fixed format independent of browser/OS locale: YYYY-MM-DD HH:mm:ss
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return value;
  }
}


export function stripHtmlTags(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  // Remove HTML tags using regex
  let text = html.replace(/<[^>]*>/g, "");
  
  // Decode common HTML entities
  const entityMap: Record<string, string> = {
    "&nbsp;": " ",
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'",
    "&apos;": "'",
    "&hellip;": "...",
    "&mdash;": "—",
    "&ndash;": "–",
  };
  
  // Replace named entities
  for (const [entity, replacement] of Object.entries(entityMap)) {
    text = text.replace(new RegExp(entity, "g"), replacement);
  }
  
  // Replace numeric entities (&#123; or &#x1F;)
  text = text.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number.parseInt(dec, 10)));
  text = text.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)));
  
  // Clean up extra whitespace
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

export function resolveStatus(status: ClusterSummaryEntry["status"]): ClusterStatusLabel {
  switch (status) {
    case "pending":
      return { label: "Pending", tone: "neutral" };
    case "analyzing":
      return { label: "Analyzing", tone: "warning" };
    case "analyzed":
      return { label: "Analyzed", tone: "positive" };
    case "failed":
      return { label: "Failed", tone: "danger" };
    default:
      return { label: "Unknown", tone: "neutral" };
  }
}

export function defaultSettings(): ClusterSettingsPayload {
  return {
    autoAnalysisEnabled: false,
    waitMinutes: 15,
    minArticles: 3,
    keywordFilters: [],
    telegramAlertsEnabled: false,
    telegramChannelId: null,
  };
}

export function sanitizeKeywords(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function collapseSeverityClass(severity: CollapseSignal["severity"]): string {
  switch (severity) {
    case "critical":
      return "collapse-severity collapse-severity--critical";
    case "warning":
      return "collapse-severity collapse-severity--warning";
    case "info":
      return "collapse-severity collapse-severity--info";
    default:
      return "collapse-severity collapse-severity--ok";
  }
}

export function collapseStatusLabel(status: CollapseMonitorResponse["summary"]["overallStatus"]): string {
  switch (status) {
    case "critical":
      return "Critical";
    case "caution":
      return "Caution";
    default:
      return "Stable";
  }
}

function FearGreedGauge({ value, annotation }: { value: number; annotation: string | null }): JSX.Element {
  // Normalize value to 0-100 range
  const normalizedValue = Math.max(0, Math.min(100, value));
  
  // Calculate angle for gauge needle (180 degrees arc: -90 to +90)
  // In SVG: 0° = right, 90° = down, -90° = left
  // For gauge: 0 (Fear) = left (-90°), 50 (Neutral) = top (0°), 100 (Greed) = right (+90°)
  // Formula: angle = (value / 100) * 180 - 90
  // Value 0 → -90° (left), Value 50 → 0° (top), Value 100 → +90° (right)
  const angle = ((normalizedValue / 100) * 180) - 90;
  
  // Determine color based on value
  let gaugeColor = "#94a3b8"; // Neutral gray
  let gradientId = "gradient-neutral";
  if (normalizedValue <= 24) {
    gaugeColor = "#ef4444"; // Extreme Fear - Red
    gradientId = "gradient-extreme-fear";
  } else if (normalizedValue <= 44) {
    gaugeColor = "#f97316"; // Fear - Orange
    gradientId = "gradient-fear";
  } else if (normalizedValue <= 55) {
    gaugeColor = "#eab308"; // Neutral - Yellow
    gradientId = "gradient-neutral";
  } else if (normalizedValue <= 75) {
    gaugeColor = "#22c55e"; // Greed - Green
    gradientId = "gradient-greed";
  } else {
    gaugeColor = "#10b981"; // Extreme Greed - Dark Green
    gradientId = "gradient-extreme-greed";
  }
  
  // Calculate arc path for filled portion
  const radius = 85; // Slightly smaller to keep needle within bounds
  const centerX = 120;
  const centerY = 120;
  const startAngle = -90; // Left side (Fear) - always start from left
  const endAngle = angle; // Current value position
  
  // Convert gauge angles to SVG angles
  const startSvgAngle = 270 + startAngle; // -90° → 180° (left)
  const endSvgAngle = 270 + endAngle;
  const startAngleRad = (startSvgAngle * Math.PI) / 180;
  const endAngleRad = (endSvgAngle * Math.PI) / 180;
  
  const x1 = centerX + radius * Math.cos(startAngleRad);
  const y1 = centerY + radius * Math.sin(startAngleRad);
  const x2 = centerX + radius * Math.cos(endAngleRad);
  const y2 = centerY + radius * Math.sin(endAngleRad);
  
  // Determine if we need large arc (when going from left to right, passing through top)
  // For values 0-50: small arc (left to top)
  // For values 50-100: large arc (left to right, passing through top)
  const largeArcFlag = normalizedValue > 50 ? 1 : 0;
  
  // Create tick marks (aligned with arc)
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const tickValue = (i / 10) * 100;
    const tickGaugeAngle = ((tickValue / 100) * 180) - 90;
    const tickSvgAngle = 270 + tickGaugeAngle;
    const tickAngleRad = (tickSvgAngle * Math.PI) / 180;
    const tickStartX = centerX + (radius - 10) * Math.cos(tickAngleRad);
    const tickStartY = centerY + (radius - 10) * Math.sin(tickAngleRad);
    const tickEndX = centerX + radius * Math.cos(tickAngleRad);
    const tickEndY = centerY + radius * Math.sin(tickAngleRad);
    
    ticks.push(
      <line
        key={i}
        x1={tickStartX}
        y1={tickStartY}
        x2={tickEndX}
        y2={tickEndY}
        stroke="rgba(148, 163, 184, 0.4)"
        strokeWidth={i % 5 === 0 ? "2" : "1"}
      />
    );
  }
  
  // Calculate needle endpoint
  // SVG coordinate system: 0° = right, 90° = down, 180° = left, 270° = up
  // Gauge system: -90° = left (Fear), 0° = up (Neutral), +90° = right (Greed)
  // Conversion: SVG_angle = 270° + gauge_angle
  let svgAngle = 270 + angle;
  if (svgAngle >= 360) svgAngle -= 360;
  if (svgAngle < 0) svgAngle += 360;
  
  const svgAngleRad = (svgAngle * Math.PI) / 180;
  // Keep needle within arc bounds (radius - 5 to prevent overflow)
  const needleLength = radius - 5;
  const needleEndX = centerX + needleLength * Math.cos(svgAngleRad);
  const needleEndY = centerY + needleLength * Math.sin(svgAngleRad);
  
  return (
    <div className="fear-greed-gauge">
      <svg viewBox="0 0 240 160" className="fear-greed-gauge__svg">
        <defs>
          <linearGradient id="gradient-extreme-fear" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="gradient-fear" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="gradient-neutral" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#eab308" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#ca8a04" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="gradient-greed" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0.4" />
          </linearGradient>
          <linearGradient id="gradient-extreme-greed" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.4" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background arc with gradient */}
        <path
          d={`M ${centerX} ${centerY} L ${centerX} ${centerY - radius} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY} Z`}
          fill="rgba(148, 163, 184, 0.08)"
          stroke="rgba(148, 163, 184, 0.15)"
          strokeWidth="1.5"
        />
        <path
          d={`M ${centerX} ${centerY} L ${centerX + radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX} ${centerY - radius} Z`}
          fill="rgba(148, 163, 184, 0.08)"
          stroke="rgba(148, 163, 184, 0.15)"
          strokeWidth="1.5"
        />
        
        {/* Tick marks */}
        {ticks}
        
        {/* Colored segments */}
        <path
          d={`M ${centerX} ${centerY} L ${centerX} ${centerY - radius} A ${radius} ${radius} 0 0 1 ${centerX + radius * 0.2} ${centerY - radius * 0.6} Z`}
          fill="rgba(239, 68, 68, 0.15)"
        />
        <path
          d={`M ${centerX} ${centerY} L ${centerX + radius * 0.2} ${centerY - radius * 0.6} A ${radius} ${radius} 0 0 1 ${centerX + radius * 0.6} ${centerY - radius * 0.2} Z`}
          fill="rgba(249, 115, 22, 0.15)"
        />
        <path
          d={`M ${centerX} ${centerY} L ${centerX + radius * 0.6} ${centerY - radius * 0.2} A ${radius} ${radius} 0 0 1 ${centerX + radius * 0.8} ${centerY + radius * 0.2} Z`}
          fill="rgba(234, 179, 8, 0.15)"
        />
        <path
          d={`M ${centerX} ${centerY} L ${centerX + radius * 0.8} ${centerY + radius * 0.2} A ${radius} ${radius} 0 0 1 ${centerX + radius * 0.6} ${centerY + radius * 0.6} Z`}
          fill="rgba(34, 197, 94, 0.15)"
        />
        <path
          d={`M ${centerX} ${centerY} L ${centerX + radius * 0.6} ${centerY + radius * 0.6} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY} Z`}
          fill="rgba(16, 185, 129, 0.15)"
        />
        
        {/* Filled arc with gradient - fills from left (Fear) to current value */}
        <path
          d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
          fill={`url(#${gradientId})`}
          opacity="0.5"
        />
        <path
          d={`M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`}
          fill="none"
          stroke={gaugeColor}
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
        />
        
        {/* Needle with shadow */}
        <g>
          {/* Shadow */}
          <line
            x1={centerX}
            y1={centerY}
            x2={needleEndX + 1}
            y2={needleEndY + 1}
            stroke="rgba(0, 0, 0, 0.3)"
            strokeWidth="4"
            strokeLinecap="round"
            opacity="0.5"
          />
          {/* Main needle */}
          <line
            x1={centerX}
            y1={centerY}
            x2={needleEndX}
            y2={needleEndY}
            stroke={gaugeColor}
            strokeWidth="3"
            strokeLinecap="round"
            filter="url(#glow)"
          />
          {/* Center circle outer */}
          <circle
            cx={centerX}
            cy={centerY}
            r="8"
            fill="rgba(15, 23, 42, 0.9)"
            stroke={gaugeColor}
            strokeWidth="2"
          />
          {/* Center circle inner */}
          <circle
            cx={centerX}
            cy={centerY}
            r="4"
            fill={gaugeColor}
          />
        </g>
        
        {/* Value text with background - positioned below center */}
        <rect
          x={centerX - 30}
          y={centerY + 18}
          width="60"
          height="26"
          rx="5"
          fill="rgba(15, 23, 42, 0.9)"
          stroke={gaugeColor}
          strokeWidth="1.5"
          opacity="0.95"
        />
        <text
          x={centerX}
          y={centerY + 35}
          textAnchor="middle"
          className="fear-greed-gauge__value"
          fill={gaugeColor}
          filter="url(#glow)"
          dominantBaseline="middle"
        >
          {Math.round(normalizedValue)}
        </text>
        
        {/* Labels */}
        <text
          x={centerX - radius - 15}
          y={centerY - radius + 5}
          textAnchor="middle"
          className="fear-greed-gauge__label"
          fill="rgba(239, 68, 68, 0.8)"
        >
          Fear
        </text>
        <text
          x={centerX + radius + 15}
          y={centerY - radius + 5}
          textAnchor="middle"
          className="fear-greed-gauge__label"
          fill="rgba(16, 185, 129, 0.8)"
        >
          Greed
        </text>
      </svg>
      {annotation && (
        <div className="fear-greed-gauge__annotation" style={{ color: gaugeColor }}>
          {annotation}
        </div>
      )}
    </div>
  );
}

export function timelineSeverityClass(severity: CollapseTimelineEvent["severity"]): string {
  switch (severity) {
    case "critical":
      return "collapse-timeline__badge collapse-timeline__badge--critical";
    case "warning":
      return "collapse-timeline__badge collapse-timeline__badge--warning";
    default:
      return "collapse-timeline__badge collapse-timeline__badge--info";
  }
}

const PINNED_SYMBOLS_STORAGE_KEY = "market-data:pinned-symbols";

function loadPinnedSymbols(): Set<string> {
  try {
    const stored = localStorage.getItem(PINNED_SYMBOLS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch (error) {
    console.warn("Failed to load pinned symbols from localStorage:", error);
  }
  return new Set();
}

function savePinnedSymbols(symbols: Set<string>): void {
  try {
    const array = Array.from(symbols);
    localStorage.setItem(PINNED_SYMBOLS_STORAGE_KEY, JSON.stringify(array));
  } catch (error) {
    console.warn("Failed to save pinned symbols to localStorage:", error);
  }
}

export function MarketDataTab(): JSX.Element {
  const watchlistQuery = useWatchlistData();
  const [pinnedSymbols, setPinnedSymbols] = useState<Set<string>>(loadPinnedSymbols);
  
  useEffect(() => {
    savePinnedSymbols(pinnedSymbols);
  }, [pinnedSymbols]);
  
  const watchlist = useMemo<WatchlistEntry[]>(() => {
    const entries = Array.isArray(watchlistQuery.data) ? watchlistQuery.data : [];
    // Sort: pinned symbols first, then others
    return [...entries].sort((a, b) => {
      const aPinned = pinnedSymbols.has(a.symbol);
      const bPinned = pinnedSymbols.has(b.symbol);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      return 0;
    });
  }, [watchlistQuery.data, pinnedSymbols]);
  const setActiveTab = useUiStore((store) => store.setActiveTab);

  const [selectedSymbolId, setSelectedSymbolId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!selectedSymbolId && watchlist.length > 0) {
      setSelectedSymbolId(watchlist[0].symbol);
    }
    if (watchlist.length === 0) {
      setSelectedSymbolId(null);
    }
  }, [selectedSymbolId, watchlist]);
  
  const handlePinToggle = (symbol: string) => {
    setPinnedSymbols((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const selectedSymbol = useMemo(() => {
    if (!selectedSymbolId) {
      return null;
    }
    return watchlist.find((item) => item.symbol === selectedSymbolId) ?? null;
  }, [selectedSymbolId, watchlist]);

  const snapshotQuery = useInstrumentSnapshot(selectedSymbol?.symbol ?? null);
  const snapshot = snapshotQuery.data as InstrumentSnapshot | undefined;

  const sentimentQuery = useSentimentData();
  const sentimentIndicators = (sentimentQuery.data?.indicators ?? []) as SentimentIndicator[];
  const liquidityFlow = (sentimentQuery.data?.liquidityFlow ?? []) as LiquidityFlowPoint[];
  
  const cryptoBubblesQuery = useCryptoBubbles();
  const cryptoBubbles = (cryptoBubblesQuery.data ?? []) as CryptoBubble[];
  const [selectedBubble, setSelectedBubble] = useState<CryptoBubble | null>(null);

  const clusterSettingsQuery = useClusterSettings();
  const [settingsDraft, setSettingsDraft] = useState<ClusterSettingsPayload>(defaultSettings);
  const [keywordInputValue, setKeywordInputValue] = useState<string>("");
  useEffect(() => {
    const payload = clusterSettingsQuery.data as ClusterSettingsResponse | undefined;
    if (payload) {
      const { autoAnalysisEnabled, waitMinutes, minArticles, keywordFilters, telegramAlertsEnabled, telegramChannelId } =
        payload;
      setSettingsDraft({
        autoAnalysisEnabled,
        waitMinutes,
        minArticles,
        keywordFilters,
        telegramAlertsEnabled,
        telegramChannelId,
      });
      setKeywordInputValue(keywordFilters.join(", "));
    }
  }, [clusterSettingsQuery.data]);

  const updateSettingsMutation = useUpdateClusterSettings();
  const [settingsNotice, setSettingsNotice] = useState<NoticeState>(null);

  const clusterSummaryQuery = useClusterSummary();
  const clusterSummary = (clusterSummaryQuery.data ?? []) as ClusterSummaryEntry[];
  const [clusterNotice, setClusterNotice] = useState<NoticeState>(null);

  const [selectedClusterSymbol, setSelectedClusterSymbol] = useState<string | null>(null);
  const clusterDetailQuery = useClusterDetail(selectedClusterSymbol);
  const clusterDetail = clusterDetailQuery.data as ClusterDetailResponse | undefined;

  const triggerMutation = useTriggerClusterAnalysis();
  const discardMutation = useDiscardCluster();
  const notifyMutation = useClusterNotification();

  const newsFeedQuery = useNewsFeed();
  const newsFeed = (newsFeedQuery.data ?? []) as NewsFeedItem[];

  const collapseMonitorQuery = useCollapseMonitor();
  const collapseMonitor = collapseMonitorQuery.data as CollapseMonitorResponse | undefined;
  const collapseActionMutation = useExecuteCollapseAction();
  const [collapseActionNotice, setCollapseActionNotice] = useState<NoticeState>(null);

  const availableChannels = (clusterSettingsQuery.data?.telegramChannels ?? []).map((channel) => channel);
  const hasTelegramChannels = availableChannels.length > 0;

  const sparkline = snapshot?.sparkline ?? [];
  const rangeMarkerWidth = snapshot ? Math.min(Math.max((snapshot.dailyRangePercent ?? 0), 10), 90) : 0;
  const orderBook = snapshot?.orderBook;
  const trades = snapshot?.trades ?? [];

  const handleClusterSettingsSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSettingsNotice(null);
    updateSettingsMutation.mutate(settingsDraft, {
      onSuccess: () => {
        setSettingsNotice({ type: "success", message: "Clustered news settings updated." });
      },
      onError: (error) => {
        setSettingsNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to update settings." });
      },
    });
  };

  const handleTriggerAnalysis = (symbol: string) => {
    setClusterNotice(null);
    triggerMutation.mutate(symbol, {
      onSuccess: () => {
        setClusterNotice({ type: "success", message: `Triggered AI analysis for ${symbol}.` });
      },
      onError: (error) => {
        setClusterNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to trigger analysis." });
      },
    });
  };

  const handleDiscardCluster = (symbol: string) => {
    setClusterNotice(null);
    discardMutation.mutate(symbol, {
      onSuccess: () => {
        setClusterNotice({ type: "success", message: `Discarded cluster for ${symbol}.` });
        setSelectedClusterSymbol((current) => (current === symbol ? null : current));
      },
      onError: (error) => {
        setClusterNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to discard cluster." });
      },
    });
  };

  const handleSendNotification = (symbol: string) => {
    setClusterNotice(null);
    notifyMutation.mutate(symbol, {
      onSuccess: () => {
        setClusterNotice({ type: "success", message: `Telegram notification dispatched for ${symbol}.` });
      },
      onError: (error) => {
        setClusterNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to send notification." });
      },
    });
  };

  const handleExecuteCollapseAction = (actionId: string, reason?: string) => {
    setCollapseActionNotice(null);
    collapseActionMutation.mutate(
      { actionId, reason },
      {
        onSuccess: (result) => {
          setCollapseActionNotice({ type: "success", message: result.message ?? "Action queued successfully." });
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : "Failed to queue emergency action. Please try again later.";
          setCollapseActionNotice({ type: "error", message });
        },
      }
    );
  };

  const renderNotice = (notice: NoticeState) =>
    notice ? <div className={`cluster-notice cluster-notice--${notice.type}`}>{notice.message}</div> : null;

  return (
    <div className="market-data-root">
      <div className="market-data-grid">
        <Card
          className="overview-card"
          title="Active watchlist"
          subtitle="Symbols monitored by running strategies. Values update every 5 seconds."
          actions={
            <Button variant="ghost" onClick={() => setActiveTab("integrations")}>
              Manage watchlist
            </Button>
          }
        >
          <div className="market-data-panel">
            {watchlistQuery.isLoading ? (
              <div className="empty-state">Loading watchlist…</div>
            ) : watchlistQuery.isError ? (
              <div className="empty-state">Unable to load watchlist. Please try again later.</div>
            ) : watchlist.length === 0 ? (
              <div className="empty-state">
                <p><strong>No watchlist symbols found.</strong></p>
                <p>
                  The watchlist displays symbols from your active spot trading portfolio. 
                  If you have holdings, ensure your trading account is connected and active in the Integrations tab.
                </p>
              </div>
            ) : (
              <table className="watchlist-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Price</th>
                    <th>24h</th>
                    <th>1h</th>
                    <th>Liquidity</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {watchlist.map((item) => (
                    <tr key={item.symbol}>
                      <td>
                        <div className="symbol-cell">
                          <span>{item.symbol}</span>
                          <span className="symbol-cell__name">{item.name}</span>
                        </div>
                      </td>
                      <td>${item.price.toLocaleString("en-US")}</td>
                      <td className={classForChange(item.change24h)}>{formatChange(item.change24h)}</td>
                      <td className={classForChange(item.change1h)}>{formatChange(item.change1h)}</td>
                      <td>
                        <span className={liquidityClass(item.liquidity)}>{item.liquidity}</span>
                      </td>
                      <td>
                        <div className="symbol-actions">
                          <Button
                            type="button"
                            variant={selectedSymbol?.symbol === item.symbol ? "primary" : "ghost"}
                            onClick={() => setSelectedSymbolId(item.symbol)}
                          >
                            View
                          </Button>
                          <Button
                            type="button"
                            variant={pinnedSymbols.has(item.symbol) ? "primary" : "ghost"}
                            onClick={() => handlePinToggle(item.symbol)}
                          >
                            {pinnedSymbols.has(item.symbol) ? "Unpin" : "Pin"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <div className="market-data-panel">
          <Card
            className="overview-card"
            title="Instrument snapshot"
            subtitle="Price action, recent trades, and system posture for the selected symbol."
          >
            {!selectedSymbol ? (
              <div className="empty-state">
                Select a symbol from the watchlist to inspect price action, venue data, and execution history.
              </div>
            ) : snapshotQuery.isLoading ? (
              <div className="empty-state">Loading instrument snapshot…</div>
            ) : snapshotQuery.isError || !snapshot ? (
              <div className="empty-state">Unable to load instrument snapshot at this time.</div>
            ) : (
              <div className="instrument-overview">
                <div className="instrument-header">
                  <div className="instrument-title">
                    <h3>{selectedSymbol.symbol}</h3>
                    <span>{selectedSymbol.name}</span>
                  </div>
                  <div className="instrument-title">
                    <div className="instrument-price">
                      ${selectedSymbol.price.toLocaleString("en-US")}
                      <span
                        className={
                          selectedSymbol.change24h >= 0
                            ? "instrument-delta instrument-delta--positive"
                            : "instrument-delta instrument-delta--negative"
                        }
                      >
                        {formatChange(selectedSymbol.change24h)} (24h)
                      </span>
                    </div>
                    <span>Primary venue: {snapshot.venue}</span>
                  </div>
                </div>

                <div className="sparkline-track" aria-hidden="true">
                  {sparkline.map((value, index) => (
                    <span key={`${snapshot.symbol}-spark-${index}`} style={{ height: `${value}%` }} />
                  ))}
                </div>

                <div className="range-indicator">
                  <strong>Daily range</strong>
                  <div className="range-bar">
                    <div className="range-marker" style={{ width: `${rangeMarkerWidth}%` }} />
                  </div>
                  <span>Positioned in the {rangeMarkerWidth > 60 ? "upper" : "lower"} percentile of the day.</span>
                </div>

                <div>
                  <strong>Order book depth</strong>
                  {orderBook && (orderBook.bids.length > 0 || orderBook.asks.length > 0) ? (
                    <div className="orderbook-grid" role="table">
                      <div className="orderbook-column" role="rowgroup">
                        <strong>Bid support</strong>
                        {orderBook.bids.map((level, index) => (
                          <div key={`bid-${level.price}-${index}`} className="orderbook-row" role="row">
                            <span role="cell">${level.price.toLocaleString("en-US")}</span>
                            <span role="cell">{level.size}</span>
                            <span role="cell">{level.venue}</span>
                          </div>
                        ))}
                      </div>
                      <div className="orderbook-column" role="rowgroup">
                        <strong>Ask pressure</strong>
                        {orderBook.asks.map((level, index) => (
                          <div key={`ask-${level.price}-${index}`} className="orderbook-row" role="row">
                            <span role="cell">${level.price.toLocaleString("en-US")}</span>
                            <span role="cell">{level.size}</span>
                            <span role="cell">{level.venue}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p><strong>Order book data unavailable.</strong></p>
                      <p>
                        Unable to fetch order book depth for {selectedSymbol.symbol} at this time. 
                        This may be due to exchange API limitations or connectivity issues.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <strong>Recent strategy trades</strong>
                  <div className="trade-log">
                    {trades.length === 0 ? (
                      <div className="empty-state">
                        <p><strong>No recent trades found.</strong></p>
                        <p>
                          Strategy executions for {selectedSymbol.symbol} will appear here once trades are executed and logged.
                        </p>
                      </div>
                    ) : (
                      trades.map((trade) => (
                        <div key={trade.id} className="trade-row">
                          <span className={trade.side === "buy" ? "trade-row__side--buy" : "trade-row__side--sell"}>
                            {trade.side.toUpperCase()}
                          </span>
                          <span>
                            {trade.quantity} @ ${trade.price.toLocaleString("en-US")}
                          </span>
                          <span>
                            {trade.venue} · {new Date(trade.time).toLocaleTimeString("en-US")}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        <Card
          className="overview-card"
          title="Market sentiment"
          subtitle="Cross-exchange signals (fear & greed, liquidity flow) aggregated in real time."
        >
          <div className="sentiment-grid">
            {sentimentQuery.isLoading ? (
              <div className="empty-state">Loading sentiment data…</div>
            ) : sentimentQuery.isError ? (
              <div className="empty-state">
                <p><strong>Unable to load sentiment indicators.</strong></p>
                <p>Please check your connection and try again later.</p>
              </div>
            ) : (
              <>
                {sentimentIndicators.map((indicator) => {
                  // Special rendering for Fear & Greed Index with custom speedometer gauge
                  if (indicator.id === "fear-greed-index") {
                    return (
                      <div key={indicator.id} className="sentiment-widget sentiment-widget--gauge">
                        <div className="sentiment-label">
                          <span>{indicator.label}</span>
                        </div>
                        <FearGreedGauge value={indicator.value} annotation={indicator.annotation ?? null} />
                      </div>
                    );
                  }
                  
                  // Default rendering for other indicators
                  return (
                    <div key={indicator.id} className="sentiment-widget">
                      <div className="sentiment-label">
                        <span>{indicator.label}</span>
                        <strong>{indicator.value}</strong>
                      </div>
                      <div className="sentiment-meter" aria-hidden="true">
                        <div className="sentiment-meter__value" style={{ width: `${indicator.value}%` }} />
                      </div>
                      {indicator.annotation ? (
                        <div className="sentiment-footer">{indicator.annotation}</div>
                      ) : null}
                    </div>
                  );
                })}

                {liquidityFlow.length > 0 ? (
                  <div className="sentiment-widget">
                    <div className="sentiment-label">
                      <span>Liquidity flow</span>
                      <span>Net inflow/outflow</span>
                    </div>
                    <div className="liquidity-cloud">
                      {liquidityFlow.map((point) => (
                        <div key={point.symbol} className={bubbleClass(point)} style={bubbleStyle(point)}>
                          <span className="liquidity-bubble__symbol">{point.symbol}</span>
                          <span className="liquidity-bubble__flow">{formatFlow(point.netFlowUsd)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {cryptoBubblesQuery.isLoading ? (
                  <div className="sentiment-widget sentiment-widget--bubbles">
                    <div className="empty-state">Loading crypto bubbles…</div>
                  </div>
                ) : cryptoBubblesQuery.isError ? (
                  <div className="sentiment-widget sentiment-widget--bubbles">
                    <div className="empty-state">
                      <p><strong>Unable to load crypto bubbles.</strong></p>
                      <p>Please check CoinMarketCap API configuration in Connections tab.</p>
                    </div>
                  </div>
                ) : cryptoBubbles.length > 0 ? (
                  <div className="sentiment-widget sentiment-widget--bubbles">
                    <div className="sentiment-label">
                      <span>Crypto Market Overview</span>
                      <span>{cryptoBubbles.length} coins</span>
                    </div>
                    <div className="crypto-bubbles-container">
                      {cryptoBubbles.slice(0, 25).map((bubble, index) => {
                        const maxMarketCap = Math.max(...cryptoBubbles.map(b => b.marketCap));
                        const sizeRatio = bubble.marketCap / maxMarketCap;
                        const size = Math.max(45, Math.min(110, 45 + sizeRatio * 65));
                        const isPositive = bubble.change24h > 0;
                        const isNegative = bubble.change24h < 0;
                        return (
                          <div
                            key={bubble.symbol}
                            className={`crypto-bubble crypto-bubble--${isPositive ? "positive" : isNegative ? "negative" : "neutral"}`}
                            style={{
                              width: `${size}px`,
                              height: `${size}px`,
                              "--index": index,
                            } as React.CSSProperties}
                            onClick={() => setSelectedBubble(bubble)}
                            title={`${bubble.name} (${bubble.symbol}) - $${bubble.price.toLocaleString("en-US")} - ${bubble.change24h > 0 ? "+" : ""}${bubble.change24h.toFixed(2)}%`}
                          >
                            <span className="crypto-bubble__symbol">{bubble.symbol}</span>
                            <span className="crypto-bubble__change">
                              {bubble.change24h > 0 ? "+" : ""}
                              {bubble.change24h.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="sentiment-widget sentiment-widget--bubbles">
                    <div className="empty-state">
                      <p><strong>No crypto market data available.</strong></p>
                      <p>
                        Add CoinMarketCap API key in Connections tab to display cryptocurrency market overview.
                      </p>
                    </div>
                  </div>
                )}

                {sentimentIndicators.length === 0 && liquidityFlow.length === 0 && cryptoBubbles.length === 0 ? (
                  <div className="empty-state">
                    Global indicators will populate once the sentiment service is connected. Configure providers in the
                    Connections tab. Liquidity bubbles will reflect inflow/outflow per symbol.
                  </div>
                ) : null}
              </>
            )}
          </div>
          
          {selectedBubble && (
            <div className="crypto-bubble-modal" onClick={() => setSelectedBubble(null)}>
              <div className="crypto-bubble-modal__content" onClick={(e) => e.stopPropagation()}>
                <div className="crypto-bubble-modal__header">
                  <h2>{selectedBubble.name} ({selectedBubble.symbol})</h2>
                  <button className="crypto-bubble-modal__close" onClick={() => setSelectedBubble(null)}>×</button>
                </div>
                <div className="crypto-bubble-modal__body">
                  <div className="crypto-bubble-modal__row">
                    <span className="crypto-bubble-modal__label">Price:</span>
                    <span className="crypto-bubble-modal__value">${selectedBubble.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</span>
                  </div>
                  <div className="crypto-bubble-modal__row">
                    <span className="crypto-bubble-modal__label">24h Change:</span>
                    <span className={`crypto-bubble-modal__value ${selectedBubble.change24h > 0 ? "positive" : selectedBubble.change24h < 0 ? "negative" : ""}`}>
                      {selectedBubble.change24h > 0 ? "+" : ""}
                      {selectedBubble.change24h.toFixed(2)}%
                    </span>
                  </div>
                  <div className="crypto-bubble-modal__row">
                    <span className="crypto-bubble-modal__label">Market Cap:</span>
                    <span className="crypto-bubble-modal__value">
                      ${(selectedBubble.marketCap / 1_000_000_000).toFixed(2)}B
                    </span>
                  </div>
                  <div className="crypto-bubble-modal__row">
                    <span className="crypto-bubble-modal__label">24h Volume:</span>
                    <span className="crypto-bubble-modal__value">
                      ${(selectedBubble.volume24h / 1_000_000).toFixed(2)}M
                    </span>
                  </div>
                  {selectedBubble.rank && (
                    <div className="crypto-bubble-modal__row">
                      <span className="crypto-bubble-modal__label">Rank:</span>
                      <span className="crypto-bubble-modal__value">#{selectedBubble.rank}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card
          className="overview-card"
          title="Live news feed"
          subtitle="Full articles streamed from your connected news providers, filtered by relevant symbols."
        >
          <div className="news-feed-container">
            {newsFeedQuery.isLoading ? (
              <div className="news-feed-empty">Loading news feed…</div>
            ) : newsFeedQuery.isError ? (
              <div className="news-feed-empty">Unable to load market news at the moment.</div>
            ) : newsFeed.length === 0 ? (
              <div className="news-feed-empty">
                <p><strong>No news articles available.</strong></p>
                <p>
                  Connect news providers in the Connections tab to start receiving market news. 
                  Articles will appear here with timestamps, impact ratings, and symbol tags.
                </p>
              </div>
            ) : (
              <div className="news-feed-scroll">
                {newsFeed.map((article) => (
                  <article key={article.id} className="news-card">
                    <div className="news-card__header">
                      <div className="news-card__source">{article.source}</div>
                      <div className="news-card__timestamp">{formatDate(article.publishedAt)}</div>
                      <Pill tone={article.impact === "high" ? "danger" : article.impact === "low" ? "neutral" : "warning"} className="news-card__impact">
                        {article.impact.toUpperCase()} IMPACT
                      </Pill>
                    </div>
                    <h3 className="news-card__title">{stripHtmlTags(article.title)}</h3>
                    {article.summary && (
                      <p className="news-card__summary">{stripHtmlTags(article.summary)}</p>
                    )}
                    <div className="news-card__footer">
                      {article.symbols.length > 0 && (
                        <div className="news-card__symbols">
                          {article.symbols.map((sym) => (
                            <Pill key={`${article.id}-${sym}`} tone="accent" className="news-card__symbol">
                              {sym}
                            </Pill>
                          ))}
                        </div>
                      )}
                      {article.clustered && (
                        <span className="news-card__badge">Clustered</span>
                      )}
                      {article.url && (
                        <a href={article.url} target="_blank" rel="noopener noreferrer" className="news-card__link">
                          Continue reading →
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="market-data-grid market-data-grid--single-column">
        <Card
          className="overview-card cluster-card-wrapper"
          title="Clustered news overview"
          subtitle="Aggregate high-impact news by symbol before triggering AI analysis."
        >
        <div className="cluster-grid cluster-grid--full">
          <form className="cluster-settings" onSubmit={handleClusterSettingsSubmit}>
            <div className="cluster-settings__header">
              <h4>Auto-analysis controls</h4>
              <label className="cluster-checkbox">
                <input
                  type="checkbox"
                  checked={settingsDraft.autoAnalysisEnabled}
                  onChange={(event) =>
                    setSettingsDraft((prev) => ({
                      ...prev,
                      autoAnalysisEnabled: event.target.checked,
                    }))
                  }
                  disabled={clusterSettingsQuery.isLoading}
                />
                Enable automatic clustering & AI analysis
              </label>
            </div>

            <div className="cluster-settings__inputs">
              <label className="cluster-field">
                <span className="cluster-field__label">Wait duration (minutes)</span>
                <input
                  type="number"
                  lang="en"
                  min={1}
                  value={settingsDraft.waitMinutes}
                  onChange={(event) => {
                    const value = event.target.value;
                    const parsed = value === "" ? 15 : Number.parseInt(value, 10);
                    if (!Number.isNaN(parsed)) {
                      setSettingsDraft((prev) => ({
                        ...prev,
                        waitMinutes: parsed,
                      }));
                    }
                  }}
                  disabled={clusterSettingsQuery.isLoading}
                />
              </label>
              <label className="cluster-field">
                <span className="cluster-field__label">Minimum articles per symbol</span>
                <input
                  type="number"
                  lang="en"
                  min={1}
                  value={settingsDraft.minArticles}
                  onChange={(event) => {
                    const value = event.target.value;
                    const parsed = value === "" ? 3 : Number.parseInt(value, 10);
                    if (!Number.isNaN(parsed)) {
                      setSettingsDraft((prev) => ({
                        ...prev,
                        minArticles: parsed,
                      }));
                    }
                  }}
                  disabled={clusterSettingsQuery.isLoading}
                />
              </label>
              <label className="cluster-field cluster-field--full">
                <span className="cluster-field__label">Keyword filters (comma separated)</span>
                <input
                  type="text"
                  value={keywordInputValue}
                  onChange={(event) => {
                    const value = event.target.value;
                    setKeywordInputValue(value);
                    setSettingsDraft((prev) => ({
                      ...prev,
                      keywordFilters: sanitizeKeywords(value),
                    }));
                  }}
                  placeholder="Hack, ETF, Rug Pull"
                  disabled={clusterSettingsQuery.isLoading}
                />
              </label>
              <div className="cluster-field cluster-field--full">
                <label className="cluster-inline-control">
                  <input
                    type="checkbox"
                    checked={settingsDraft.telegramAlertsEnabled}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        telegramAlertsEnabled: event.target.checked,
                      }))
                    }
                    disabled={!hasTelegramChannels || clusterSettingsQuery.isLoading}
                  />
                  <span>Enable Telegram alerts for analyzed clusters</span>
                </label>
                {!hasTelegramChannels ? (
                  <span className="cluster-field__note cluster-field__note--warning">
                    Connect a Telegram channel in Integrations to enable alerts.
                  </span>
                ) : null}
              </div>
              <div className="cluster-field cluster-field--full">
                <span className="cluster-field__label">Telegram channel</span>
                {!hasTelegramChannels ? (
                  <span className="cluster-field__note">No Telegram channels linked.</span>
                ) : null}
                <select
                  value={settingsDraft.telegramChannelId ?? ""}
                  onChange={(event) =>
                    setSettingsDraft((prev) => ({
                      ...prev,
                      telegramChannelId: event.target.value ? event.target.value : null,
                    }))
                  }
                  disabled={!hasTelegramChannels || clusterSettingsQuery.isLoading}
                >
                  <option value="">Select channel</option>
                  {availableChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="cluster-settings__footer">
              <Button type="submit" variant="primary" disabled={updateSettingsMutation.isPending}>
                {updateSettingsMutation.isPending ? "Saving…" : "Update settings"}
              </Button>
              <span className="cluster-settings__meta">
                Last applied: {clusterSettingsQuery.isFetching ? "Updating…" : formatDate(clusterSettingsQuery.data?.updatedAt ?? null)}
              </span>
            </div>
            {renderNotice(settingsNotice)}
          </form>

          <div className="cluster-monitoring-container">
            <div className="cluster-monitoring-header">
              <h4>Monitoring activity</h4>
              <span>
                Tracking {clusterSummary.length} symbol{clusterSummary.length === 1 ? "" : "s"}
              </span>
            </div>
            {renderNotice(clusterNotice)}
            {clusterSummaryQuery.isLoading ? (
              <div className="news-feed-empty">Loading cluster summary…</div>
            ) : clusterSummaryQuery.isError ? (
              <div className="news-feed-empty">Unable to load cluster summary right now.</div>
            ) : clusterSummary.length === 0 ? (
              <div className="news-feed-empty">
                <p><strong>No news clusters available.</strong></p>
                <p>
                  Clusters will appear once news articles are collected and grouped by symbol. 
                  Ensure news providers are connected and active in the Connections tab.
                </p>
              </div>
            ) : (
              <div className="cluster-monitoring-scroll">
                {clusterSummary.map((cluster) => {
                  const statusLabel = resolveStatus(cluster.status);
                  return (
                    <article key={cluster.symbol} className="cluster-monitoring-card">
                      <div className="cluster-monitoring-card__header">
                        <div className="cluster-monitoring-card__symbol">{cluster.symbol}</div>
                        <Pill tone={statusLabel.tone === "positive" ? "accent" : statusLabel.tone === "warning" ? "warning" : statusLabel.tone === "danger" ? "danger" : "neutral"} className="cluster-monitoring-card__status">
                          {statusLabel.label}
                        </Pill>
                        <div className="cluster-monitoring-card__timestamp">
                          {formatDate(cluster.latestTriggerAt)}
                        </div>
                      </div>
                      <div className="cluster-monitoring-card__body">
                        <div className="cluster-monitoring-card__info">
                          <div className="cluster-monitoring-card__info-item">
                            <span className="cluster-monitoring-card__info-label">Articles:</span>
                            <span className="cluster-monitoring-card__info-value">{cluster.articleCount}</span>
                          </div>
                          <div className="cluster-monitoring-card__info-item">
                            <span className="cluster-monitoring-card__info-label">Next:</span>
                            <span className="cluster-monitoring-card__info-value">{formatDate(cluster.nextAutoAnalysisAt)}</span>
                          </div>
                          {cluster.lastError && (
                            <div className="cluster-monitoring-card__info-item cluster-monitoring-card__info-item--error">
                              <span className="cluster-monitoring-card__info-label">Error:</span>
                              <span className="cluster-monitoring-card__info-value">{cluster.lastError}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="cluster-monitoring-card__footer">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setSelectedClusterSymbol(cluster.symbol)}
                        >
                          View
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          disabled={triggerMutation.isPending}
                          onClick={() => handleTriggerAnalysis(cluster.symbol)}
                        >
                          {triggerMutation.isPending ? "Triggering…" : "Analyze"}
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        {selectedClusterSymbol ? (
          <div className="cluster-drawer" role="dialog" aria-modal="true">
            <div className="cluster-drawer__content">
              <div className="cluster-drawer__header">
                <div>
                  <h3>Cluster detail · {selectedClusterSymbol}</h3>
                  <span>Articles pending analysis and AI outcomes for this symbol.</span>
                </div>
                <Button type="button" variant="ghost" onClick={() => setSelectedClusterSymbol(null)}>
                  Close
                </Button>
              </div>

              {clusterDetailQuery.isLoading ? (
                <div className="empty-state">Loading cluster detail…</div>
              ) : clusterDetailQuery.isError || !clusterDetail ? (
                <div className="empty-state">Unable to load cluster details for this symbol.</div>
              ) : (
                <div className="cluster-drawer__body">
                  <div className="cluster-drawer__section">
                    <h4>Articles</h4>
                    {clusterDetail.articles.length === 0 ? (
                      <div className="empty-state">
                        <p><strong>No articles found for this cluster.</strong></p>
                        <p>Articles may still be processing or have been filtered out.</p>
                      </div>
                    ) : (
                      <div className="cluster-articles">
                        {clusterDetail.articles.map((article: ClusterArticle) => (
                          <article key={article.id} className="cluster-article-card">
                            <div className="cluster-article-card__header">
                              <span>{article.source}</span>
                              <span>·</span>
                              <span>{formatDate(article.publishedAt)}</span>
                              <span>·</span>
                              <span className={`impact-pill impact-pill--${article.impact}`}>{article.impact}</span>
                            </div>
                            <div className="cluster-article-card__title">{article.title}</div>
                            {article.summary ? <p>{article.summary}</p> : null}
                            {article.keywordsMatched.length > 0 ? (
                              <div className="cluster-article-card__keywords">
                                {article.keywordsMatched.map((keyword) => (
                                  <Pill key={`${article.id}-${keyword}`} tone="neutral">
                                    {keyword}
                                  </Pill>
                                ))}
                              </div>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="cluster-drawer__section">
                    <h4>Latest AI summary</h4>
                    {clusterDetail.aiSummary ? (
                      <div className="cluster-summary-card">
                        <div className="cluster-summary-card__meta">
                          <span>{formatDate(clusterDetail.aiSummary.analyzedAt)}</span>
                          <Pill tone="accent">{clusterDetail.aiSummary.recommendation.toUpperCase()}</Pill>
                          <span>Confidence: {(clusterDetail.aiSummary.confidence * 100).toFixed(1)}%</span>
                        </div>
                        <p>{clusterDetail.aiSummary.text}</p>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p><strong>No AI analysis available yet.</strong></p>
                        <p>
                          AI analysis will appear here once the cluster reaches the minimum article threshold and analysis is triggered.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="cluster-drawer__section cluster-drawer__actions">
                    <Button
                      type="button"
                      variant="primary"
                      disabled={triggerMutation.isPending}
                      onClick={() => selectedClusterSymbol && handleTriggerAnalysis(selectedClusterSymbol)}
                    >
                      {triggerMutation.isPending ? "Triggering…" : "Analyze"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={discardMutation.isPending}
                      onClick={() => selectedClusterSymbol && handleDiscardCluster(selectedClusterSymbol)}
                    >
                      {discardMutation.isPending ? "Discarding…" : "Discard"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={notifyMutation.isPending || !clusterDetail.telegram?.enabled}
                      onClick={() => selectedClusterSymbol && handleSendNotification(selectedClusterSymbol)}
                    >
                      {notifyMutation.isPending ? "Sending…" : "Notify"}
                    </Button>
                    {clusterDetail.telegram ? (
                      <div className="cluster-drawer__telegram">
                        <strong>Telegram status</strong>
                        <span>Channel: {clusterDetail.telegram.channelId ?? "Not configured"}</span>
                        <span>Last notification: {formatDate(clusterDetail.telegram.lastNotificationAt)}</span>
                        {clusterDetail.telegram.lastError ? (
                          <span className="cluster-drawer__telegram-error">{clusterDetail.telegram.lastError}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
        </Card>
      </div>

      <div className="market-data-grid market-data-grid--single-column">
        <Card
          className="overview-card"
          title="Market collapse monitor"
          subtitle="Cross-signal aggregator highlighting conditions that precede systemic drawdowns."
        >
          {collapseMonitorQuery.isLoading ? (
            <div className="empty-state">Loading collapse monitor data…</div>
          ) : collapseMonitorQuery.isError ? (
            <div className="empty-state">Unable to load collapse monitor insights right now.</div>
          ) : !collapseMonitor ? (
            <div className="empty-state">
              <p><strong>Collapse monitor not initialized.</strong></p>
              <p>
                Collapse signals will appear once supporting data sources (news, social, on-chain, derivatives) are configured and active.
              </p>
            </div>
          ) : (
            <>
              <div className="collapse-grid">
                <section className="collapse-signals">
                  <header className="collapse-signals__header">
                    <div>
                      <h4>Real-time signals</h4>
                      <span>
                        {collapseMonitor.summary.triggeredSignals} active signal
                        {collapseMonitor.summary.triggeredSignals === 1 ? "" : "s"} · updated{" "}
                        {formatDate(collapseMonitor.summary.lastUpdatedAt)}
                      </span>
                    </div>
                    <span className={`collapse-summary collapse-summary--${collapseMonitor.summary.overallStatus}`}>
                      {collapseStatusLabel(collapseMonitor.summary.overallStatus)}
                    </span>
                  </header>
                  <div className="collapse-signals__table">
                    <table>
                      <thead>
                        <tr>
                          <th>Signal</th>
                          <th>Value</th>
                          <th>Threshold</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collapseMonitor.signals.map((signal) => (
                          <tr key={signal.id}>
                            <td>
                              <div className="collapse-signal__label">
                                <span>{signal.label}</span>
                                {signal.description ? (
                                  <span className="collapse-signal__description">{signal.description}</span>
                                ) : null}
                                {signal.dataSource ? (
                                  <span className="collapse-signal__source">Source: {signal.dataSource}</span>
                                ) : null}
                              </div>
                            </td>
                            <td>{signal.value ?? "—"}</td>
                            <td>{signal.threshold ?? "—"}</td>
                            <td>
                              <span className={collapseSeverityClass(signal.severity)}>
                                {signal.triggered ? "Triggered" : "Idle"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <aside className="collapse-sidebar">
                  <section className="collapse-sources">
                    <h5>Source integration status</h5>
                    <ul>
                      {collapseMonitor.sources.map((source) => (
                        <li key={source.id}>
                          <div className={`collapse-source__status collapse-source__status--${source.status}`}>
                            <span>{source.name}</span>
                            <span>{source.provider ?? "—"}</span>
                          </div>
                          <div className="collapse-source__meta">
                            <span>{source.details ?? "No additional notes"}</span>
                            <span>
                              {source.lastCheckedAt ? formatDate(source.lastCheckedAt) : "Not yet checked"}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className="collapse-triggers">
                    <h5>Triggered automation rules</h5>
                    <ul>
                      {collapseMonitor.triggers.map((trigger) => (
                        <li key={trigger.id}>
                          <div className="collapse-trigger__header">
                            <span>{trigger.title}</span>
                            <span className={collapseSeverityClass(trigger.severity)}>
                              {trigger.satisfied ? "Active" : "Idle"}
                            </span>
                          </div>
                          <p>{trigger.description}</p>
                          {trigger.matchedSignals.length > 0 ? (
                            <div className="collapse-trigger__signals">
                              Matched signals: {trigger.matchedSignals.join(", ")}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className="collapse-actions">
                    <h5>Emergency actions</h5>
                    {renderNotice(collapseActionNotice)}
                    <ul>
                      {collapseMonitor.actions.map((action: CollapseAction) => (
                        <li key={action.id}>
                          <div className="collapse-action__header">
                            <div>
                              <span>{action.label}</span>
                              <p>{action.description}</p>
                            </div>
                            <span className={`collapse-action__status collapse-action__status--${action.status}`}>
                              {action.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="collapse-action__meta">
                            <span>Category: {action.category}</span>
                            <span>
                              Last executed: {action.lastExecutedAt ? formatDate(action.lastExecutedAt) : "—"}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant={action.isDangerous ? "primary" : "ghost"}
                            disabled={!action.supported || collapseActionMutation.isPending}
                            onClick={() => handleExecuteCollapseAction(action.id, undefined)}
                          >
                            {collapseActionMutation.isPending ? "Queuing…" : "Execute action"}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </section>
                </aside>
              </div>

              <section className="collapse-timeline">
                <div className="collapse-timeline__header">
                  <h5>Collapse timeline</h5>
                  <span>Recent alerts, AI summaries, and automated actions</span>
                </div>
                {collapseMonitor.timeline.length === 0 ? (
                  <div className="empty-state">
                    Timeline events will populate once collapse monitor collects actionable data.
                  </div>
                ) : (
                  <ul>
                    {collapseMonitor.timeline.map((event: CollapseTimelineEvent) => (
                      <li key={event.id}>
                        <div className="collapse-timeline__row">
                          <div className="collapse-timeline__content">
                            <span className={timelineSeverityClass(event.severity)}>{event.category}</span>
                            <strong>{event.title}</strong>
                            <span className="collapse-timeline__timestamp">{formatDate(event.timestamp)}</span>
                            {event.summary ? <p>{event.summary}</p> : null}
                          </div>
                          {event.metrics && Array.isArray(event.metrics) && event.metrics.length > 0 ? (
                            <div className="collapse-timeline__metrics">
                              {event.metrics.map((metric) => (
                                <div key={`${event.id}-${metric.label}`}>
                                  <span>{metric.label}</span>
                                  <strong>{metric.value}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </Card>
      </div>

    </div>
  );
}

